import React, { useState, useMemo, useEffect } from "react";
import { Search, Plus, Calendar, BookOpen, Eye, Edit2 } from "lucide-react";
import { ModernTable, Pagination, formatCurrency, HeroBanner } from "../../../components/ui";
import EmptyState from "../../../components/ui/EmptyState";
import { BookEntryModal } from "../components/BookEntryModal";
import { useContacts } from "../../contacts/hooks/useContacts";
import toast from "react-hot-toast";
import { bookEntryService } from "../../customer_stages/work_space/services/book_entry_service";
import api from "../../../services/api";

export default function BookEntryScreen() {
  const { contacts = [] } = useContacts();
  const [addOpen, setAddOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // "add" | "edit" | "view"
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [search, setSearch] = useState("");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  const [filters, setFilters] = useState({
    dateRange: null, // 'T', 'W', 'M', 'Y'
    fromDate: "",
    toDate: "",
    status: "all"
  });

  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Map of customerId -> { progress, lastUpdated }
  const [trackOrderMap, setTrackOrderMap] = useState({});

  const fetchEntries = async () => {
    setIsLoading(true);
    try {
      const res = await bookEntryService.getAllBookEntries();
      if (res && res.data) {
        const mappedData = res.data.map(entry => {
          const cust = entry.customer_id || {};
          return {
            id: entry._id,
            sdId: cust.sdId || cust.sd_id || cust.customId || "—",
            customerId: cust._id,
            customerName: cust.customerName || `${cust.firstName || ""} ${cust.lastName || ""}`.trim(),
            contact: cust.phone || cust.phoneNumber,
            customerType: cust.salesType || cust.customerType,
            grandTotal: entry.grand_total,
            materialAmount: entry.material,
            transport: entry.transport,
            carpenterAmount: entry.carpenter,
            iron: entry.iron,
            hardware: entry.hardware,
            puPaints: entry.pu_paints,
            designAmount: entry.design,
            isUPVC: entry.is_upvc,
            upvcAmount: entry.upvc,
            notes: entry.notes,
            createdAt: entry.createdAt,
            updatedAt: entry.updatedAt
          };
        });
        setEntries(mappedData);
      }
    } catch (error) {
      console.error("Failed to fetch book entries", error);
      toast.error("Failed to load book entries");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  // Fetch track order progress for all entries after they load
  useEffect(() => {
    if (entries.length === 0) return;
    const uniqueCustomerIds = [...new Set(entries.map(e => e.customerId).filter(Boolean))];
    Promise.allSettled(
      uniqueCustomerIds.map(cId =>
        api.get(`/track-order/customer/${cId}`).then(res => ({ cId, data: res.data?.data }))
      )
    ).then(results => {
      const map = {};
      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value?.data) {
          const { cId, data } = r.value;
          const assignments = data.assignments || [];
          if (assignments.length === 0) { map[cId] = { progress: 0, lastUpdated: null }; return; }
          // Average progress across all assigned members
          const progresses = assignments.map(a => {
            const updates = a.updates || [];
            return updates.length > 0 ? updates[updates.length - 1].progress : 0;
          });
          const avg = Math.round(progresses.reduce((s, p) => s + p, 0) / progresses.length);
          // Find the latest update timestamp
          let lastUpdated = null;
          assignments.forEach(a => {
            (a.updates || []).forEach(u => {
              const t = new Date(u.updatedAt || u.createdAt);
              if (!lastUpdated || t > lastUpdated) lastUpdated = t;
            });
          });
          map[cId] = { progress: avg, lastUpdated };
        }
      });
      setTrackOrderMap(map);
    });
  }, [entries]);

  const filteredEntries = useMemo(() => {
    let result = [...entries];

    // 1. Search filter (Name, SD ID, Contact)
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(e => {
        const name = (e.customerName || "").toLowerCase();
        const sdId = (e.sdId || "").toLowerCase();
        const contact = (e.contact || "").toLowerCase();
        return name.includes(q) || sdId.includes(q) || contact.includes(q);
      });
    }

    // 2. Preset Date Range Filter (T, W, M, Y)
    if (filters.dateRange) {
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      result = result.filter(e => {
        if (!e.createdAt) return false;
        const createdDate = new Date(e.createdAt);
        const diffTime = now - createdDate;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        if (filters.dateRange === 'T') {
          return createdDate >= startOfDay;
        } else if (filters.dateRange === 'W') {
          return diffDays <= 7;
        } else if (filters.dateRange === 'M') {
          return diffDays <= 30;
        } else if (filters.dateRange === 'Y') {
          return diffDays <= 365;
        }
        return true;
      });
    }

    // 3. Custom Date Picker Filter (Exact Date)
    if (filters.exactDate) {
      const exactDateObj = new Date(filters.exactDate);
      const startOfDay = new Date(exactDateObj);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(exactDateObj);
      endOfDay.setHours(23, 59, 59, 999);
      
      result = result.filter(e => {
        if (!e.createdAt) return false;
        const createdTime = new Date(e.createdAt).getTime();
        return createdTime >= startOfDay.getTime() && createdTime <= endOfDay.getTime();
      });
    }


    return result;
  }, [entries, search, filters]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, filters]);

  const totalPages = Math.ceil(filteredEntries.length / itemsPerPage);
  const paginatedEntries = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredEntries.slice(start, start + itemsPerPage);
  }, [filteredEntries, page]);

  const handleView = (entry) => {
    setSelectedEntry(entry);
    setModalMode("view");
    setAddOpen(true);
  };

  const handleEdit = (entry) => {
    setSelectedEntry(entry);
    setModalMode("edit");
    setAddOpen(true);
  };

  const handleAddClick = () => {
    setSelectedEntry(null);
    setModalMode("add");
    setAddOpen(true);
  };

  const handleSaveEntry = async (savedData) => {
    // We don't save from this screen anymore since the button is removed.
    // If we ever need to, we'd call the API here and then fetchEntries().
    setAddOpen(false);
  };

  const columns = [
    {
      header: "SD ID",
      key: "sdId",
      className: "!py-1.5 !px-3",
      render: (row) => (
        <span className="text-[12px] font-semibold text-slate-700 whitespace-nowrap">
          {row.sdId || "—"}
        </span>
      )
    },
    {
      header: "Customer Info",
      key: "customerInfo",
      className: "!py-1.5 !px-3",
      render: (row) => {
        const cust = contacts.find(c => c._id === row.customerId);
        const name = cust ? (cust.customerName || `${cust.firstName || ""} ${cust.lastName || ""}`.trim()) : row.customerName;
        const contact = cust ? (cust.phone || cust.phoneNumber) : row.contact;
        const type = cust ? (cust.salesType || cust.customerType) : row.customerType;
        return (
          <div className="flex flex-col gap-0.5 py-1 min-w-[170px]">
            <span className="font-semibold text-slate-800 text-[13px] tracking-tight">{name || "—"}</span>
            <span className="text-[11px] font-medium text-slate-500">
              {(type || "—") + " | " + (contact || "—")}
            </span>
          </div>
        );
      }
    },
    {
      header: "Total",
      key: "grandTotal",
      className: "!py-1.5 !px-3",
      render: (row) => (
        <span className="text-[12px] font-bold text-slate-900 whitespace-nowrap">
          {formatCurrency(row.grandTotal || 0)}
        </span>
      )
    },
    {
      header: "Material Amount",
      key: "materialAmount",
      className: "!py-1.5 !px-3",
      render: (row) => (
        <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">
          {formatCurrency(row.materialAmount || 0)}
        </span>
      )
    },
    {
      header: "Carpenter + Iron",
      key: "carpenterAndIron",
      className: "!py-1.5 !px-3",
      render: (row) => {
        const total = (Number(row.carpenterAmount) || 0) + (Number(row.iron) || 0);
        return (
          <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">
            {formatCurrency(total)}
          </span>
        );
      }
    },
    {
      header: "UPVC",
      key: "upvcAmount",
      className: "!py-1.5 !px-3",
      render: (row) => (
        <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">
          {row.isUPVC ? formatCurrency(row.upvcAmount || 0) : "—"}
        </span>
      )
    },
    {
      header: "Others",
      key: "othersTotal",
      className: "!py-1.5 !px-3",
      render: (row) => {
        const othersTotal = 
          (Number(row.transport) || 0) + 
          (Number(row.hardware) || 0) + 
          (Number(row.puPaints) || 0) + 
          (Number(row.designAmount) || 0);
        return (
          <span className="text-[12px] font-medium text-slate-600 whitespace-nowrap">
            {formatCurrency(othersTotal)}
          </span>
        );
      }
    },
    {
      header: "Notes",
      key: "notes",
      className: "!py-1.5 !px-3",
      render: (row) => (
        <p className="text-[11px] text-slate-500 max-w-[150px] truncate" title={row.notes}>
          {row.notes || "—"}
        </p>
      )
    },
    {
      header: "Created",
      key: "createdAt",
      className: "!py-1.5 !px-3",
      render: (row) => (
        <span className="text-[11px] font-semibold text-slate-400 whitespace-nowrap">
          {row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
        </span>
      )
    },
    {
      header: "Order Progress",
      key: "orderProgress",
      className: "!py-1.5 !px-3",
      render: (row) => {
        const info = trackOrderMap[row.customerId];
        const progress = info?.progress ?? null;
        const lastUpdated = info?.lastUpdated;
        if (progress === null) {
          return <span className="text-[11px] text-slate-400">—</span>;
        }
        const barColor = progress === 100
          ? 'bg-emerald-500'
          : progress >= 50
          ? 'bg-indigo-500'
          : 'bg-amber-400';
        return (
          <div className="min-w-[120px]">
            <div className="flex items-center justify-between mb-1">
              <span className={`text-[11px] font-semibold ${
                progress === 100 ? 'text-emerald-600' : 'text-slate-700'
              }`}>{progress}%</span>
              {progress === 100 && (
                <span className="text-[10px] text-emerald-600 font-medium">Done</span>
              )}
            </div>
            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${barColor} rounded-full transition-all`} style={{ width: `${progress}%` }} />
            </div>
            {lastUpdated && (
              <p className="text-[10px] text-slate-400 mt-1">
                {new Date(lastUpdated).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
              </p>
            )}
          </div>
        );
      }
    },

    {
      header: "Actions",
      key: "actions",
      className: "!py-1.5 !px-3",
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <button 
            type="button"
            onClick={(e) => { e.stopPropagation(); handleView(row); }}
            className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all focus:outline-none"
            title="View details"
          >
            <Eye size={15} />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="flex flex-col gap-6 w-full max-w-full pb-2 overflow-x-hidden">
      {/* Page Header */}
      <HeroBanner
        title="Space Designs Book an Entry"
        subtitle="Manage customer bookings and payment allocations"
      />

      {/* Toolbar / Control Panel */}
      <div className="flex items-center justify-between gap-4 w-full bg-transparent pb-2 overflow-x-auto custom-scrollbar">
        <div className="flex items-center gap-2">
          {/* Search Box */}
          <div className="flex items-center gap-2 relative">
            <button 
              type="button"
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              className={`flex items-center justify-center w-9 h-9 border rounded-lg transition-colors shrink-0 shadow-sm ${isSearchExpanded ? 'bg-slate-100 border-gray-300 text-slate-800' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
            >
              <Search size={16} />
            </button>
            
            {isSearchExpanded && (
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, SD ID, contact..."
                className="h-9 px-3 border border-gray-200 rounded-lg bg-white text-gray-700 text-[13px] font-medium focus:outline-none focus:border-slate-400 shadow-sm shrink-0 w-48 transition-all"
                autoFocus
              />
            )}
          </div>

          {/* Date Picker: Exact */}
          <div className="flex items-center gap-1.5 h-9 px-3 border border-gray-200 rounded-lg bg-white text-gray-700 text-[13px] font-medium shadow-sm shrink-0 relative">
            <Calendar size={14} className="text-gray-400" />
            <span className="text-gray-400 text-[11px] font-semibold select-none">Date:</span>
            <input
              type="date"
              value={filters.exactDate || ""}
              onChange={(e) => setFilters(prev => ({ ...prev, exactDate: e.target.value, dateRange: null }))}
              className="outline-none border-none text-[12px] bg-white text-gray-700 font-semibold cursor-pointer w-28"
            />
            {filters.exactDate && (
              <button 
                type="button"
                onClick={() => setFilters(prev => ({ ...prev, exactDate: "" }))}
                className="text-gray-400 hover:text-red-500 font-bold ml-1 text-xs"
                title="Clear"
              >
                ×
              </button>
            )}
          </div>

          {/* T W M Y Buttons Group */}
          <div className="flex items-center gap-1 shrink-0 ml-1">
            {['T', 'W', 'M', 'Y'].map(label => {
              const isActive = filters.dateRange === label;
              return (
                <button 
                  key={label} 
                  type="button"
                  onClick={() => {
                    const newRange = isActive ? null : label;
                    setFilters(prev => ({ ...prev, dateRange: newRange, exactDate: "" }));
                  }}
                  className={`w-9 h-9 flex items-center justify-center border rounded-lg text-[12px] font-bold transition-all shadow-sm ${isActive ? 'bg-[#0f172a] text-white border-[#0f172a]' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                >
                  {label}
                </button>
              );
            })}
          </div>


        </div>


      </div>

      {/* Entries List / Grid */}
      <div className="flex flex-col bg-white border border-slate-200/80 rounded-xl overflow-hidden shadow-sm">
        {filteredEntries.length === 0 ? (
          <EmptyState 
            icon="📒"
            title="No Book Entries Found"
            description="Create a new entry to track payment allocations and booking amounts."
            action={null}
          />
        ) : (
          <>
            <div className="w-full bg-transparent relative z-10 overflow-x-auto">
              <ModernTable 
                columns={columns}
                data={paginatedEntries}
                isLoading={isLoading}
              />
            </div>
            
            <div className="border-t border-[#ECE7DE] bg-white py-2 px-4 border-x border-b">
              <Pagination 
                page={page} 
                totalPages={totalPages || 1} 
                onPageChange={setPage} 
              />
            </div>
          </>
        )}
      </div>

      {/* Book Entry Dialog */}
      <BookEntryModal 
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSave={handleSaveEntry}
        mode={modalMode}
        initialData={selectedEntry}
      />
    </div>
  );
}
